import { Button } from '@trellis/roots/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@trellis/roots/dialog'
import { useIsCoarsePointer } from '@trellis/roots/lib/useIsCoarsePointer'
import { Typography } from '@trellis/roots/typography'
import { Camera, FileText, Paperclip, Upload, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { useDeleteReceiptMutation, useDisclosure, useExpenseReceiptQuery } from '@/hooks'
import { cn } from '@/lib/utils'

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
const MAX_SIZE_MB = 10
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024 // server compresses images; PDFs stored as-is
const IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp']
const CAMERA_ACCEPT = IMAGE_MIME.join(',')

const ACCEPTED_FILE_TYPES_LABEL = 'JPEG, PNG, WEBP, or PDF'
const RECEIPT_FILE_REQUIREMENTS_LABEL = `${ACCEPTED_FILE_TYPES_LABEL} · max ${MAX_SIZE_MB} MB`

/**
 * Single source of truth for the receipt requirements copy — reused by every caller's
 * header.
 */
export const RECEIPT_UPLOAD_DESCRIPTION = `Attach a photo or PDF of the receipt (${RECEIPT_FILE_REQUIREMENTS_LABEL}).`

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file || !IMAGE_MIME.includes(file.type)) {
      setUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])
  return url
}

interface ReceiptUploadProps {
  /** Undefined when creating a new expense — prevents GCS delete calls. */
  expenseId: string | undefined
  hasReceipt: boolean
  receiptFilename: string | null
  receiptMimeType: string | null
  receiptSizeBytes: number | null
  /** Controlled: the selected file awaiting upload. Uploaded when the form is saved. */
  pendingFile: File | null
  onPendingFileChange: (file: File | null) => void
  /**
   * Bridges the gap between pendingFile being cleared and the receipt query delivering
   * the new signed URL. Set from the upload response immediately after a successful
   * save.
   *
   * Undefined = no committed value yet (fall through to receipt query) null = fallback
   * when the upload response returned no signed URL string = signed URL from the most
   * recent successful upload
   */
  committedReceiptUrl?: string | null
  /** When true, all upload and remove actions are hidden. */
  readOnly?: boolean
}

export function ReceiptUpload({
  expenseId,
  hasReceipt,
  receiptFilename,
  receiptMimeType,
  pendingFile,
  onPendingFileChange,
  committedReceiptUrl,
  readOnly,
}: ReceiptUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const isCoarsePointer = useIsCoarsePointer()
  const uploadDialog = useDisclosure()
  const [draftFile, setDraftFile] = useState<File | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  const deleteMutation = useDeleteReceiptMutation()

  const receiptQuery = useExpenseReceiptQuery(expenseId ?? '', hasReceipt && !pendingFile)
  const existingReceiptUrl = receiptQuery.data?.data?.receiptUrls?.sm ?? null

  const pendingPreviewUrl = useObjectUrl(pendingFile)
  const draftPreviewUrl = useObjectUrl(draftFile)

  function validateFile(file: File): boolean {
    if (!ACCEPTED_MIME.includes(file.type)) {
      toast.error('Unsupported file type', {
        description: `Use ${ACCEPTED_FILE_TYPES_LABEL}.`,
      })
      return false
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('File too large', { description: `Maximum size is ${MAX_SIZE_MB} MB.` })
      return false
    }
    return true
  }

  function handleFileInputChange(changeEvent: React.ChangeEvent<HTMLInputElement>) {
    const file = changeEvent.target.files?.[0]
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!file) return
    if (validateFile(file)) setDraftFile(file)
  }

  function handleCameraInputChange(changeEvent: React.ChangeEvent<HTMLInputElement>) {
    const file = changeEvent.target.files?.[0]
    if (cameraInputRef.current) cameraInputRef.current.value = ''
    if (!file) return
    if (validateFile(file)) onPendingFileChange(file)
  }

  function handleDragOver(dragEvent: React.DragEvent<HTMLDivElement>) {
    dragEvent.preventDefault()
    setIsDragOver(true)
  }

  function handleDragLeave(dragEvent: React.DragEvent<HTMLDivElement>) {
    dragEvent.preventDefault()
    setIsDragOver(false)
  }

  function handleDrop(dragEvent: React.DragEvent<HTMLDivElement>) {
    dragEvent.preventDefault()
    setIsDragOver(false)
    const file = dragEvent.dataTransfer.files[0]
    if (!file) return
    if (validateFile(file)) setDraftFile(file)
  }

  function handleOpenDialog() {
    setDraftFile(pendingFile)
    uploadDialog.open(true)
  }

  function handleConfirm() {
    if (!draftFile) return
    onPendingFileChange(draftFile)
    uploadDialog.close()
  }

  function handleDialogOpenChange(isOpen: boolean) {
    if (!isOpen) setDraftFile(null)
    uploadDialog.onOpenChange(isOpen)
  }

  async function handleRemoveExistingReceipt() {
    if (!expenseId) return
    setConfirmDeleteOpen(false)
    try {
      await deleteMutation.mutateAsync(expenseId)
      toast.success('Receipt removed')
    } catch (caughtError) {
      toast.error('Failed to remove receipt', {
        description:
          caughtError instanceof Error ? caughtError.message : 'Please try again',
      })
    }
  }

  const isDeleting = deleteMutation.isPending
  const showReceipt = !!pendingFile || hasReceipt
  const displayIsImage = pendingFile
    ? IMAGE_MIME.includes(pendingFile.type)
    : receiptMimeType
      ? IMAGE_MIME.includes(receiptMimeType)
      : false
  const resolvedReceiptUrl =
    committedReceiptUrl !== undefined ? committedReceiptUrl : existingReceiptUrl
  const compactPreviewUrl = pendingFile ? pendingPreviewUrl : resolvedReceiptUrl
  const displayFilename = pendingFile?.name ?? receiptFilename
  const draftIsImage = !!draftFile && IMAGE_MIME.includes(draftFile.type)

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 rounded-lg border p-3">
        {showReceipt ? (
          <div className="flex w-full flex-col items-center gap-3 text-center">
            {displayIsImage && compactPreviewUrl ? (
              <img
                src={compactPreviewUrl}
                alt="Receipt thumbnail"
                className="size-12 shrink-0 rounded border object-cover"
              />
            ) : (
              <div className="flex size-12 shrink-0 items-center justify-center rounded border">
                <FileText className="text-muted-foreground size-6" />
              </div>
            )}

            <div className="min-w-0 flex-1">
              {displayFilename && (
                <Typography variant="body-sm" className="truncate font-medium">
                  {displayFilename}
                </Typography>
              )}
              {pendingFile && (
                <Typography variant="body-sm" color="muted">
                  Pending — will upload on save
                </Typography>
              )}
            </div>

            {!readOnly && (
              <div className="flex shrink-0 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={handleOpenDialog}
                >
                  <Upload className="mr-1.5 size-3.5" />
                  {pendingFile ? 'Change' : 'Replace'}
                </Button>
                {isCoarsePointer && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isDeleting}
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <Camera className="mr-1.5 size-3.5" />
                    Retake
                  </Button>
                )}
                {pendingFile ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onPendingFileChange(null)}
                  >
                    <X className="mr-1.5 size-3.5" />
                    Clear
                  </Button>
                ) : confirmDeleteOpen ? (
                  <div className="flex gap-1.5">
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={isDeleting}
                      onClick={handleRemoveExistingReceipt}
                    >
                      Yes, remove
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmDeleteOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isDeleting}
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    <X className="mr-1.5 size-3.5" />
                    Remove
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex w-full flex-col items-center justify-center gap-2 py-8">
            <div className="flex size-12 shrink-0 items-center justify-center rounded border">
              <Paperclip className="text-muted-foreground size-6" />
            </div>
            <div className="flex-1">
              <Typography variant="body-sm" color="muted">
                No receipt attached
              </Typography>
            </div>
            {!readOnly && (
              <div className="flex flex-wrap justify-center gap-2">
                {isCoarsePointer && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <Camera className="mr-1.5 size-3.5" />
                    Take photo
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant={isCoarsePointer ? 'outline' : 'default'}
                  onClick={handleOpenDialog}
                >
                  <Upload className="mr-1.5 size-3.5" />
                  Upload receipt
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={uploadDialog.isOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {showReceipt ? 'Replace Receipt' : 'Upload Receipt'}
            </DialogTitle>
            <DialogDescription>
              Attach a photo or PDF ({RECEIPT_FILE_REQUIREMENTS_LABEL}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div
              className={cn(
                'flex cursor-pointer flex-col items-center gap-4 rounded-lg border-2 border-dashed p-8 text-center transition-colors',
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50',
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(keyboardEvent) => {
                if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                  fileInputRef.current?.click()
                }
              }}
            >
              {draftFile ? (
                draftIsImage && draftPreviewUrl ? (
                  <img
                    src={draftPreviewUrl}
                    alt="Receipt preview"
                    className="max-h-48 max-w-full rounded-md border object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="text-muted-foreground size-10" />
                    <Typography
                      variant="body-sm"
                      color="muted"
                      className="max-w-xs truncate"
                    >
                      {draftFile.name}
                    </Typography>
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="text-muted-foreground size-10" />
                  <div>
                    <Typography variant="body-sm" className="font-medium">
                      Drag & drop or click to browse
                    </Typography>
                    <Typography variant="body-sm" color="muted" className="mt-0.5">
                      {RECEIPT_FILE_REQUIREMENTS_LABEL}
                    </Typography>
                  </div>
                </div>
              )}
            </div>

            {draftFile && (
              <div className="bg-muted flex items-center gap-2 rounded-md px-3 py-2">
                <div className="min-w-0 flex-1 overflow-hidden">
                  <Typography variant="body-sm" className="truncate font-medium">
                    {draftFile.name}
                  </Typography>
                  <Typography variant="body-sm" color="muted">
                    Will upload when you save the expense
                  </Typography>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation()
                    setDraftFile(null)
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirm} disabled={!draftFile}>
              Select File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={handleFileInputChange}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept={CAMERA_ACCEPT}
        capture="environment"
        className="hidden"
        onChange={handleCameraInputChange}
      />
    </div>
  )
}
